function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, e) { if ("object" != typeof t || !t) return t; var r; if ("undefined" != typeof Symbol && void 0 !== (r = t[Symbol.toPrimitive])) { var i = r.call(t, e || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === e ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Form Web Component: iamSellingPlanInput <iam-selling-plan-input>
// Extends: iamFormElement
// ----------------------------------------------------------------------------
// The Selling Plan Input is responsible for handling the selling plan selection UI.
// ----------------------------------------------------------------------------
// Attributes:
// - `data-frequencies="monthly"` → only monthly subscription plans are offered (main-product
//   "Frequencies shown", used by Shoplift A/B duplicates). Ignored in the cart, and when the
//   product has no monthly plan.
// ----------------------------------------------------------------------------
// Event Listeners:
// - `productForm:variant:changed` → Dynamically refreshes selling plans on variant change
// ----------------------------------------------------------------------------
// Gotchas:
// - Unknown support with Preorders, multiple selling plan groups, or plans with >1 option input
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import { extractFrequencyFromText } from 'scripts/helpers/selling-plan-frequency';
import iamFormElement from 'scripts/classes/iamFormElement';
import translate from 'scripts/helpers/iamota-translate';
import { Loadable } from 'scripts/classes/iamota-component-helpers';
import { formatMoney } from '@shopify/theme-currency';
import Debug from 'scripts/libs/debug';

// Styles
import './_selling-plan-input.scss';
class iamSellingPlanInput extends iamFormElement {
  constructor() {
    var _window, _window2, _window3, _window4;
    super();
    this.tabIndex = 0;
    this.addEventListener('keydown', this.handleKeydown.bind(this));
    this.loadable = new Loadable(this);
    this.sellingPlanGroups = [];
    this.selectedPlan = null;
    this.cartItem = null;
    this.cartContext = false;
    this.frequencyFiltered = false;
    this.translations = {
      purchase_options: (_window = window) === null || _window === void 0 || (_window = _window.translations) === null || _window === void 0 ? void 0 : _window.sellingPlan.purchase_options,
      onetime: (_window2 = window) === null || _window2 === void 0 || (_window2 = _window2.translations) === null || _window2 === void 0 ? void 0 : _window2.sellingPlan.onetime,
      group_plan_label: (_window3 = window) === null || _window3 === void 0 || (_window3 = _window3.translations) === null || _window3 === void 0 ? void 0 : _window3.sellingPlan.group_plan_label,
      error: (_window4 = window) === null || _window4 === void 0 || (_window4 = _window4.translations) === null || _window4 === void 0 ? void 0 : _window4.sellingPlan.error
    };
  }
  getCurrentSellingPlan() {
    return this.selectedPlan || null;
  }
  onFormInit() {
    this.cartItem = this.closest('iam-cart-item');
    this.cartContext = Boolean(this.cartItem);
    if (!this.hasAttribute('data-no-input')) {
      this.hiddenInput = this.form.querySelector('input[name="selling_plan"]');
      if (!this.hiddenInput) {
        this.hiddenInput = document.createElement('input');
        this.hiddenInput.type = 'hidden';
        this.hiddenInput.name = 'selling_plan';
        this.hiddenInput.value = '';
        this.form.appendChild(this.hiddenInput);
      }
    }
    var initRefresh = () => this.refreshSellingPlans();
    if (this.cartItem && !this.cartItem.itemData) {
      var handler = () => {
        initRefresh();
        iamotaEventHandler.unsubscribe('cartForm:items:item:init', handler, {
          context: this.cartItem
        });
      };
      iamotaEventHandler.subscribe('cartForm:items:item:init', handler, {
        context: this.cartItem
      });
    } else {
      initRefresh();
    }
    iamotaEventHandler.subscribe('productForm:variant:changed', () => {
      this.refreshSellingPlans();
    }, {
      context: this.iamForm
    });
  }
  refreshSellingPlans() {
    this.loadable.setLoading(true);
    try {
      var currentData = this.cartItem ? this.cartItem.getCurrentData() : this.iamForm.getCurrentData();

      // Guard against missing product or variant data
      if (!(currentData !== null && currentData !== void 0 && currentData.product) || !(currentData !== null && currentData !== void 0 && currentData.variant)) {
        if (!this.cartItem) {
          this.showError(this.translations.error);
        }
        return;
      }
      this.sellingPlanGroups = this.preprocessSellingPlans(currentData);
      if (!this.sellingPlanGroups.length) {
        this.showError(this.translations.error);
        return;
      }
      this.render();
      this.bindEvents();
    } catch (error) {
      console.error('Selling Plan Processing Error:', error);
      this.showError(this.translations.error);
    } finally {
      this.loadable.setLoading(false);
    }
  }
  preprocessSellingPlans() {
    var currentData = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var variant = currentData.variant,
      product = currentData.product;
    var groups = [];
    if (!variant || !product) {
      return groups;
    }
    if (!product.requires_selling_plan) {
      groups.push({
        id: 'onetime',
        name: this.translations.onetime,
        name_with_discount: this.translations.onetime,
        options: [],
        plans: [this.createOnetimePlan(variant)]
      });
    }
    (product.selling_plan_groups || []).forEach(group => {
      var discounts = group.selling_plans.map(plan => {
        var adj = plan.price_adjustments[0];
        return "".concat(adj.value_type, ":").concat(adj.value);
      });
      var uniqueDiscounts = [...new Set(discounts)];
      var commonDiscountAdj = uniqueDiscounts.length === 1 ? group.selling_plans[0].price_adjustments[0] : null;
      var nameWithDiscount = translate('{{ name }} {{ common_discount }}', 'sellingPlan.sellingplan', _objectSpread(_objectSpread({}, group), {}, {
        common_discount: this.formatDiscountLabel(commonDiscountAdj)
      }));
      var plans = group.selling_plans.map(plan => {
        var _variant$selling_plan;
        var allocation = (_variant$selling_plan = variant.selling_plan_allocations) === null || _variant$selling_plan === void 0 ? void 0 : _variant$selling_plan.find(a => a.selling_plan_id === plan.id);
        var adj = plan.price_adjustments[0];
        var purchaseType = plan.recurring_deliveries ? 'subscription' : 'selling_plan';
        var optionMapping = (plan.options || []).reduce((mapping, option) => {
          mapping[option.name] = option.value;
          return mapping;
        }, {});
        var primaryOption = plan.options[0] || {};
        var rawLabel = "".concat(primaryOption.name || '', " ").concat(primaryOption.value || '').trim();
        var formattedDiscount = this.formatDiscountLabel(adj);
        var planData = {
          id: plan.id,
          purchase_type: purchaseType,
          group_id: group.id,
          group_name: group.name,
          group_name_with_discount: nameWithDiscount,
          optionMapping: optionMapping,
          label: rawLabel,
          label_with_discount: translate('{{ label }} (Save {{ formatted_discount }})', 'sellingPlan.plan_label', _objectSpread(_objectSpread({}, plan), {}, {
            label: rawLabel,
            formatted_discount: formattedDiscount
          })),
          name: plan.name || group.name,
          description: plan.description,
          discount: {
            value_type: adj.value_type,
            value: adj.value,
            name: formattedDiscount
          },
          price: (allocation === null || allocation === void 0 ? void 0 : allocation.price) || variant.price,
          compare_at_price: (allocation === null || allocation === void 0 ? void 0 : allocation.compare_at_price) || variant.compare_at_price,
          per_delivery_price: (allocation === null || allocation === void 0 ? void 0 : allocation.per_delivery_price) || (allocation === null || allocation === void 0 ? void 0 : allocation.price) || variant.price,
          recurring_deliveries: plan.recurring_deliveries
        };
        return this.applyCuratedPriceOverride(planData);
      });
      groups.push({
        id: group.id,
        name: group.name,
        name_with_discount: nameWithDiscount,
        options: group.options,
        plans: plans
      });
    });
    Debug.extreme('Processed Selling Plan Groups:', groups);
    return this.filterByFrequency(groups);
  }

  // Monthly-only mode: drop every subscription plan that isn't monthly (and any group left
  // empty). Leaves the groups untouched when nothing monthly would remain, so a product
  // without a monthly plan never ends up with no way to subscribe.
  filterByFrequency(groups) {
    this.frequencyFiltered = false;
    if (this.cartContext || this.dataset.frequencies !== 'monthly') return groups;
    var filtered = groups.map(group => group.id === 'onetime' ? group : _objectSpread(_objectSpread({}, group), {}, {
      plans: group.plans.filter(plan => this.readPlanFrequency(plan) === 1)
    })).filter(group => group.plans.length);
    if (!filtered.some(group => group.id !== 'onetime')) return groups;
    var planCount = list => list.reduce((count, group) => count + group.plans.length, 0);
    this.frequencyFiltered = planCount(filtered) < planCount(groups);
    return filtered;
  }
  createOnetimePlan(variant) {
    var planData = {
      id: null,
      purchase_type: 'onetime',
      group_id: 'onetime',
      group_name: this.translations.onetime,
      group_name_with_discount: this.translations.onetime,
      optionMapping: {},
      label: this.translations.onetime,
      label_with_discount: this.translations.onetime,
      name: this.translations.onetime,
      description: null,
      discount: null,
      price: variant.price,
      compare_at_price: variant.compare_at_price,
      per_delivery_price: variant.price,
      recurring_deliveries: false
    };
    return this.applyCuratedPriceOverride(planData);
  }
  getCuratedPresetPrice() {
    var _this$iamForm, _this$iamForm2;
    if (this.cartContext) return null;
    if (!((_this$iamForm = this.iamForm) !== null && _this$iamForm !== void 0 && _this$iamForm.isCurated)) return null;
    var curatedPrice = Number((_this$iamForm2 = this.iamForm) === null || _this$iamForm2 === void 0 ? void 0 : _this$iamForm2.presetMonthSelectionVariantPrice);
    return Number.isFinite(curatedPrice) ? curatedPrice : null;
  }
  applyCuratedPriceOverride(planData) {
    var curatedPrice = this.getCuratedPresetPrice();
    if (!Number.isFinite(curatedPrice)) return planData;
    return _objectSpread(_objectSpread({}, planData), {}, {
      price: curatedPrice,
      compare_at_price: null,
      per_delivery_price: curatedPrice
    });
  }
  formatDiscountLabel(adj) {
    if (!adj) return '';
    if (adj.value_type === 'percentage') {
      return "".concat(adj.value, "%");
    }
    if (adj.value_type === 'fixed_amount') {
      return formatMoney(adj.value);
    }
    return adj.value;
  }
  updateDisplayedSubscriptionPrice(plan) {
    var priceSpan = this.querySelector('.js-plan-price');
    if (!priceSpan || !plan) return;
    var formattedPrice = formatMoney(plan.per_delivery_price || plan.price);
    var formattedCompare = plan.compare_at_price ? formatMoney(plan.compare_at_price) : null;
    if (formattedCompare && formattedCompare !== formattedPrice) {
      priceSpan.innerHTML = "<s class=\"price__sale\">".concat(formattedCompare, "</s> <span class=\"price__original\">").concat(formattedPrice, "</span>");
    } else {
      priceSpan.textContent = formattedPrice;
    }
  }
  getRequestedFrequencyFromUrl() {
    var _window$location;
    if (typeof window === 'undefined' || !((_window$location = window.location) !== null && _window$location !== void 0 && _window$location.search)) return null;
    var params = new URLSearchParams(window.location.search);
    var rawFrequency = String(params.get('frequency') || '').trim().toLowerCase();
    if (!rawFrequency) return null;
    if (rawFrequency === 'monthly') return 1;
    if (rawFrequency === 'bimonthly') return 2;
    if (rawFrequency === '1' || rawFrequency === '2') return Number(rawFrequency);
    return null;
  }
  readPlanFrequency() {
    var plan = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    var optionValues = Object.values(plan.optionMapping || {});
    for (var value of optionValues) {
      var parsed = extractFrequencyFromText(value);
      if (parsed) return parsed;
    }
    var fallbackValues = [plan.label, plan.name, plan.group_name];
    for (var _value of fallbackValues) {
      var _parsed = extractFrequencyFromText(_value);
      if (_parsed) return _parsed;
    }
    return null;
  }
  findSubscriptionPlanByFrequency(requestedFrequency) {
    if (!requestedFrequency) return null;
    var allSubscriptionPlans = this.sellingPlanGroups.filter(group => group.id !== 'onetime').flatMap(group => group.plans);
    return allSubscriptionPlans.find(plan => this.readPlanFrequency(plan) === requestedFrequency) || null;
  }
  render() {
    var _this$cartItem;
    var noDropdowns = this.hasAttribute('data-no-dropdowns');
    var oneTimeGroup = this.sellingPlanGroups.find(g => g.id === 'onetime');
    var subscriptionGroups = this.sellingPlanGroups.filter(g => g.id !== 'onetime');
    var allSubscriptionPlans = subscriptionGroups.flatMap(g => g.plans);
    var idPrefix = ((_this$cartItem = this.cartItem) === null || _this$cartItem === void 0 || (_this$cartItem = _this$cartItem.itemData) === null || _this$cartItem === void 0 ? void 0 : _this$cartItem.key) || Math.random().toString(36).substr(2, 9);
    if (this.cartContext) {
      var _dropdownId = "selling-plan-dropdown-".concat(idPrefix);
      var optionsHtml = "\n                ".concat(oneTimeGroup ? "<option value=\"onetime\">".concat(oneTimeGroup.name, "</option>") : '', "\n                ").concat(allSubscriptionPlans.map(plan => "<option value=\"".concat(plan.id, "\">").concat(plan.group_name, "</option>")).join(''), "\n            ");
      this.innerHTML = "\n                <div class=\"selling-plan__dropdown-container\">\n                    <label class=\"selling-plan-dropdown__label\" for=\"".concat(_dropdownId, "\">").concat(this.translations.group_plan_label, "</label>\n                    <div class=\"selling-plan__dropdown-wrapper\">\n                        <select class=\"selling-plan__dropdown\" id=\"").concat(_dropdownId, "\">\n                            ").concat(optionsHtml, "\n                        </select>\n                    </div>\n                </div>\n            ");
      this.autoSelectDefaultPlan();
      return;
    }

    // Skip rendering if there's only one plan (e.g., only onetime or a single subscription).
    // Not when monthly-only filtering left that one plan: the subscription box is where a
    // subscription product shows its price, so it stays (with a one-option dropdown).
    if (!this.frequencyFiltered && this.sellingPlanGroups.length === 1 && this.sellingPlanGroups[0].plans.length === 1) {
      this.selectedPlan = this.sellingPlanGroups[0].plans[0];
      if (this.hiddenInput) this.hiddenInput.value = this.selectedPlan.id || '';
      iamotaEventHandler.trigger("".concat(this.namespace, ":changed"), {
        sellingPlan: this.selectedPlan
      }, {
        context: this
      });
      return;
    }
    var html = '';
    var radioName = "purchase_type_".concat(idPrefix);
    var subscriptionId = "subscription_".concat(idPrefix);
    var onetimeId = "onetime_".concat(idPrefix);
    var dropdownId = "selling-plan-dropdown-".concat(idPrefix);
    if (allSubscriptionPlans.length > 0) {
      var firstPlan = allSubscriptionPlans[0];
      var formattedPrice = formatMoney(firstPlan.per_delivery_price || firstPlan.price);
      var formattedCompare = firstPlan.compare_at_price ? formatMoney(firstPlan.compare_at_price) : null;
      var subscriptionPriceMarkup = formattedCompare && formattedCompare !== formattedPrice ? "<s class=\"price__sale\">".concat(formattedCompare, "</s> <span class=\"price__original\">").concat(formattedPrice, "</span>") : "<span class=\"price__original\">".concat(formattedPrice, "</span>");
      html += "\n                <div class=\"selling-plan__option\">\n                    <div class=\"selling-plan__details\">\n                        <span class=\"selling-plan__details-label\">\n                            <input type=\"radio\" name=\"".concat(radioName, "\" value=\"subscription\" id=\"").concat(subscriptionId, "\">\n                            <label for=\"").concat(subscriptionId, "\">").concat(this.translations.purchase_options, "</label>\n                        </span>\n                        <span class=\"js-plan-price selling-plan__price\">\n                            ").concat(subscriptionPriceMarkup, "\n                        </span>\n                    </div>\n\n                    ").concat(!noDropdowns ? "\n                        <div class=\"selling-plan__dropdown-container\">\n                            <label class=\"selling-plan-dropdown__label\" for=\"".concat(dropdownId, "\">").concat(this.translations.group_plan_label, "</label>\n                            <div class=\"selling-plan__dropdown-wrapper\">\n                                <select class=\"selling-plan__dropdown\" id=\"").concat(dropdownId, "\" data-group=\"subscription\" disabled aria-disabled=\"true\">\n                                    ").concat(allSubscriptionPlans.map(plan => "\n                                        <option value=\"".concat(plan.id, "\">").concat(plan.group_name, "</option>\n                                    ")).join(''), "\n                                </select>\n                            </div>\n                        </div>\n                    ") : '', "\n                </div>\n            ");
    }
    if (oneTimeGroup) {
      html += "\n                <div class=\"selling-plan__option one-time\">\n                    <div class=\"selling-plan__details\">\n                        <span class=\"selling-plan__details-label\">\n                            <input type=\"radio\" name=\"".concat(radioName, "\" value=\"onetime\" id=\"").concat(onetimeId, "\">\n                            <label for=\"").concat(onetimeId, "\">").concat(this.translations.onetime, "</label>\n                        </span>\n                        ").concat(oneTimeGroup.plans.map(plan => "\n                            <span class=\"selling-plan__price\">".concat(formatMoney(plan.price), "</span>\n                        ")).join(''), "\n                    </div>\n                </div>\n            ");
    }
    this.innerHTML = "\n            <div class=\"selling-plan\" role=\"radiogroup\" aria-label=\"".concat(this.translations.purchase_options, "\">\n                ").concat(html, "\n            </div>\n        ");
    if (noDropdowns) {
      var _this$selectedPlan;
      this.selectedPlan = (oneTimeGroup === null || oneTimeGroup === void 0 ? void 0 : oneTimeGroup.plans[0]) || allSubscriptionPlans[0] || null;
      if (this.hiddenInput) this.hiddenInput.value = ((_this$selectedPlan = this.selectedPlan) === null || _this$selectedPlan === void 0 ? void 0 : _this$selectedPlan.id) || '';
      iamotaEventHandler.trigger("".concat(this.namespace, ":changed"), {
        sellingPlan: this.selectedPlan
      }, {
        context: this
      });
    }
    this.autoSelectDefaultPlan();
  }
  bindEvents() {
    if (this.cartContext) {
      var _dropdown = this.querySelector('.selling-plan__dropdown');
      _dropdown === null || _dropdown === void 0 || _dropdown.addEventListener('change', () => {
        this.resolveSelectedPlan();
      });
      return;
    }
    var radios = this.querySelectorAll('input[type="radio"][name^="purchase_type_"]');
    var dropdown = this.querySelector('.selling-plan__dropdown');
    var noDropdowns = this.hasAttribute('data-no-dropdowns');
    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (!noDropdowns && dropdown) {
          var isSubscription = radio.value === 'subscription';
          dropdown.disabled = !isSubscription;
          dropdown.setAttribute('aria-disabled', String(!isSubscription));
        }
        this.resolveSelectedPlan();
      });
    });
    if (!noDropdowns && dropdown) {
      dropdown.addEventListener('change', () => {
        this.resolveSelectedPlan();
      });
    }
  }
  resolveSelectedPlan() {
    var _this$selectedPlan2, _this$selectedPlan3;
    if (this.cartContext) {
      var dropdown = this.querySelector('.selling-plan__dropdown');
      var value = dropdown === null || dropdown === void 0 ? void 0 : dropdown.value;
      var _plan;
      if (value === 'onetime') {
        var group = this.sellingPlanGroups.find(g => g.id === 'onetime');
        _plan = group === null || group === void 0 ? void 0 : group.plans[0];
      } else {
        var allPlans = this.sellingPlanGroups.filter(g => g.id !== 'onetime').flatMap(g => g.plans);
        _plan = allPlans.find(p => p.id == value);
      }
      this.selectedPlan = _plan || null;
      iamotaEventHandler.trigger("".concat(this.namespace, ":changed"), {
        sellingPlan: this.selectedPlan
      }, {
        context: this
      });
      return;
    }
    var selectedRadio = this.querySelector('input[type="radio"][name^="purchase_type_"]:checked');
    if (!selectedRadio) return;
    var groupId = selectedRadio.value;
    var plan;

    // Reset selected class on all options
    this.querySelectorAll('.selling-plan__option').forEach(option => {
      option.classList.remove('is-selected');
    });

    // Add selected class to the chosen one
    var selectedOption = selectedRadio.closest('.selling-plan__option');
    if (selectedOption) {
      selectedOption.classList.add('is-selected');
    }
    if (groupId === 'onetime') {
      var _group = this.sellingPlanGroups.find(g => g.id === 'onetime');
      plan = _group === null || _group === void 0 ? void 0 : _group.plans[0];
    } else if (groupId === 'subscription') {
      var _allPlans = this.sellingPlanGroups.filter(g => g.id !== 'onetime').flatMap(g => g.plans);
      if (this.hasAttribute('data-no-dropdowns')) {
        plan = _allPlans[0];
      } else {
        var _dropdown2 = this.querySelector('select[data-group="subscription"]');
        var selectedPlanId = _dropdown2 === null || _dropdown2 === void 0 ? void 0 : _dropdown2.value;
        plan = _allPlans.find(p => p.id == selectedPlanId);
      }
    }
    this.selectedPlan = plan || null;
    if (this.hiddenInput) this.hiddenInput.value = ((_this$selectedPlan2 = this.selectedPlan) === null || _this$selectedPlan2 === void 0 ? void 0 : _this$selectedPlan2.id) || '';
    iamotaEventHandler.trigger("".concat(this.namespace, ":changed"), {
      sellingPlan: this.selectedPlan
    }, {
      context: this
    });

    // Update price span
    if (((_this$selectedPlan3 = this.selectedPlan) === null || _this$selectedPlan3 === void 0 ? void 0 : _this$selectedPlan3.purchase_type) === 'subscription') {
      this.updateDisplayedSubscriptionPrice(this.selectedPlan);
    }
  }
  autoSelectDefaultPlan() {
    var _this$cartItem3;
    if (this.cartContext) {
      var _this$cartItem2;
      var dropdown = this.querySelector('.selling-plan__dropdown');
      if (!dropdown) return;
      var _currentPlanId = ((_this$cartItem2 = this.cartItem) === null || _this$cartItem2 === void 0 || (_this$cartItem2 = _this$cartItem2.itemData) === null || _this$cartItem2 === void 0 || (_this$cartItem2 = _this$cartItem2.selling_plan_allocation) === null || _this$cartItem2 === void 0 || (_this$cartItem2 = _this$cartItem2.selling_plan) === null || _this$cartItem2 === void 0 ? void 0 : _this$cartItem2.id) || null;
      if (_currentPlanId) {
        dropdown.value = _currentPlanId;
      } else {
        dropdown.value = 'onetime';
      }
      this.resolveSelectedPlan();
      return;
    }
    var currentPlanId = ((_this$cartItem3 = this.cartItem) === null || _this$cartItem3 === void 0 || (_this$cartItem3 = _this$cartItem3.itemData) === null || _this$cartItem3 === void 0 || (_this$cartItem3 = _this$cartItem3.selling_plan_allocation) === null || _this$cartItem3 === void 0 || (_this$cartItem3 = _this$cartItem3.selling_plan) === null || _this$cartItem3 === void 0 ? void 0 : _this$cartItem3.id) || null;
    var targetGroup;
    var targetPlan;
    if (currentPlanId) {
      var _targetGroup;
      // Cart item already has a plan selected—respect it.
      targetGroup = this.sellingPlanGroups.find(g => g.plans.some(p => p.id == currentPlanId));
      targetPlan = (_targetGroup = targetGroup) === null || _targetGroup === void 0 ? void 0 : _targetGroup.plans.find(p => p.id == currentPlanId);
    } else if (this.cartItem) {
      var _targetGroup2;
      // Editing a cart item without a subscription defaults to one-time purchase.
      targetGroup = this.sellingPlanGroups.find(g => g.id === 'onetime');
      targetPlan = (_targetGroup2 = targetGroup) === null || _targetGroup2 === void 0 ? void 0 : _targetGroup2.plans[0];
    } else {
      var requestedFrequency = this.getRequestedFrequencyFromUrl();
      var allSubscriptionPlans = this.sellingPlanGroups.filter(group => group.id !== 'onetime').flatMap(group => group.plans);
      // Any explicit ?frequency= link still preselects the matching
      // subscription plan; without it the page defaults to One Time.
      var requestedPlan = requestedFrequency ? (requestedFrequency !== 1 ? allSubscriptionPlans[1] : allSubscriptionPlans[0]) || allSubscriptionPlans[0] : null;
      if (requestedPlan) {
        targetGroup = this.sellingPlanGroups.find(g => g.id !== 'onetime' && g.plans.some(p => p.id == requestedPlan.id));
        targetPlan = requestedPlan;
      } else {
        var _targetGroup3;
        // Product page: default to One Time so customers aren't opted
        // into a subscription.
        targetGroup = this.sellingPlanGroups.find(g => g.id === 'onetime');
        targetPlan = (_targetGroup3 = targetGroup) === null || _targetGroup3 === void 0 ? void 0 : _targetGroup3.plans[0];
      }
      if (!targetGroup) {
        var _targetGroup4;
        // Subscription-only products have no one-time group; keep the
        // first subscription plan.
        targetGroup = this.sellingPlanGroups.find(g => g.id !== 'onetime');
        targetPlan = (_targetGroup4 = targetGroup) === null || _targetGroup4 === void 0 ? void 0 : _targetGroup4.plans[0];
      }
    }
    if (!targetGroup) return;
    var targetValue = targetGroup.id === 'onetime' ? 'onetime' : 'subscription';
    var targetRadio = this.querySelector("input[type=\"radio\"][name^=\"purchase_type_\"][value=\"".concat(targetValue, "\"]"));
    if (targetRadio) {
      var _this$selectedPlan4;
      targetRadio.checked = true;
      if (targetValue === 'subscription' && !this.hasAttribute('data-no-dropdowns')) {
        var _dropdown3 = this.querySelector('select[data-group="subscription"]');
        if (_dropdown3) {
          _dropdown3.disabled = false;
          _dropdown3.setAttribute('aria-disabled', 'false');
          if (targetPlan) _dropdown3.value = targetPlan.id;
        }
      }
      this.resolveSelectedPlan();
      if (((_this$selectedPlan4 = this.selectedPlan) === null || _this$selectedPlan4 === void 0 ? void 0 : _this$selectedPlan4.purchase_type) === 'subscription') {
        this.updateDisplayedSubscriptionPrice(this.selectedPlan);
      }
    }
  }
  handleKeydown(event) {
    if (event.target !== this) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    var radios = Array.from(this.querySelectorAll('input[type="radio"][name^="purchase_type_"]'));
    if (!radios.length) return;
    var currentIndex = radios.findIndex(r => r.checked);
    currentIndex = (currentIndex + 1) % radios.length;
    var nextRadio = radios[currentIndex];
    nextRadio.checked = true;
    nextRadio.dispatchEvent(new Event('change', {
      bubbles: true
    }));
    nextRadio.focus();
  }
  showError(message) {
    var _this$iamForm3;
    (_this$iamForm3 = this.iamForm) === null || _this$iamForm3 === void 0 || _this$iamForm3.setErrorState(message);
  }
}
customElements.define('iam-selling-plan-input', iamSellingPlanInput);
export default iamSellingPlanInput;